import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ClipboardEdit, CalendarOff, Palmtree, UserX } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import EntryForm from "../components/EntryForm";
import DeleteEntryDialog from "../components/DeleteEntryDialog";
import { DayStatusCard, DayStatusForm } from "../components/DayStatusPanel";

const MODES = [
  ["perf", "Update Performance", "I attended customers / walk-ins", ClipboardEdit],
  ["off", "I am on Week Off / Leave", "I was not working", CalendarOff],
  ["no_walkin", "No Walk-in / No Customer Attended", "I worked but attended no customer", UserX],
];
const ZERO = { walkins: 0, bills: 0, sales: 0, gms: 0, mto: 0, rts: 0, npg: 0, categories: {}, qty: {} };

function NoWalkin({ date, entry, onSaved }) {
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/entries", { ...ZERO, date, no_walkin: true, remarks: "" });
      toast.success("Recorded: No walk-in today");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (entry?.no_walkin) return <div className="rounded-2xl bg-slate-100 border border-slate-200 p-5 text-slate-700" data-testid="no-walkin-recorded"><b>No Walk-in</b> recorded for {fmtDate(date)}. This counts as a working day with zero performance.</div>;
  return (
    <div className="bs-card p-5 space-y-4" data-testid="no-walkin-form">
      <div className="font-display text-xl font-bold text-[#011E38]">No walk-in · {fmtDate(date)}</div>
      <p className="text-sm text-slate-500">You were present and working but attended no customer. Walk-ins, bills, sales, GMS, MTO, RTS, NPG and products will be recorded as 0. This day still counts as a working day.</p>
      {entry && <div className="text-sm text-amber-800 bg-amber-50 rounded-xl px-4 py-3" data-testid="no-walkin-replace-note">This will replace the performance you already submitted for this day with zeros.</div>}
      <Button disabled={busy} onClick={save} className="w-full h-14 text-lg rounded-2xl bg-[#011E38] hover:bg-[#032C52]" data-testid="no-walkin-submit-button">Confirm No Walk-in</Button>
    </div>
  );
}

function OffPicker({ date, onSaved }) {
  const [kind, setKind] = useState(null);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2" data-testid="off-kind-buttons">
        {[["week_off", "WEEK OFF", CalendarOff], ["leave", "ON LEAVE", Palmtree]].map(([k, l, Icon]) => (
          <button key={k} type="button" onClick={() => setKind(k)} data-testid={`off-kind-${k}`}
            className={`h-14 rounded-xl border-2 font-bold flex items-center justify-center gap-2 transition-colors ${kind === k ? "border-[#011E38] bg-[#011E38] text-white" : "border-slate-200 text-[#011E38]"}`}>
            <Icon className="w-5 h-5" /> {l}
          </button>
        ))}
      </div>
      {kind && <DayStatusForm key={kind + date} date={date} status={kind} onSaved={onSaved} />}
    </div>
  );
}

export default function DailyEntry() {
  const { settings } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [date, setDate] = useState(params.get("date") || settings.effective_date);
  const [cats, setCats] = useState([]);
  const [entry, setEntry] = useState(null);
  const [ds, setDs] = useState(null);
  const [closed, setClosed] = useState(false);
  const [mode, setMode] = useState(null);
  const [del, setDel] = useState(false);

  const load = useCallback(async () => {
    const [c, e, s] = await Promise.all([api.get("/categories"), api.get("/entries", { params: { date } }), api.get("/day-status", { params: { date } })]);
    setCats(c.data);
    const en = e.data[0] || null;
    setEntry(en);
    setDs(s.data || null);
    setClosed(!!en?.closed);
    setMode(en?.no_walkin ? "no_walkin" : en || params.get("date") ? "perf" : null);
  }, [date, params]);

  useEffect(() => { load(); }, [load]);

  const hasStatus = ds && ds.leave_status !== "rejected";
  const blockMsg = mode === "off" && entry ? "You already submitted an entry for this day. Delete it first to mark Week Off or Leave."
    : mode === "off" && hasStatus ? "This day already has a status. Clear it above to change it." : "";

  return (
    <div className="space-y-6 bs-rise">
      <div>
        <div className="bs-eyebrow">Daily entry</div>
        <h1 className="font-display text-4xl font-extrabold text-[#011E38] tracking-tight mt-1">Today's Entry</h1>
      </div>
      <label className="block">
        <span className="text-sm font-semibold text-slate-600">Date</span>
        <Input type="date" value={date} max={settings.effective_date} onChange={(e) => e.target.value && setDate(e.target.value)}
          className="mt-1.5 h-14 text-lg" data-testid="entry-date-input" />
      </label>
      {closed && (
        <div className="rounded-xl bg-slate-100 border border-slate-200 p-4 flex gap-3 text-slate-700" data-testid="entry-closed-banner">
          <Lock className="w-5 h-5 shrink-0" /> This day is closed by your manager. Ask them to reopen it to make changes.
        </div>
      )}
      {ds && <DayStatusCard ds={ds} closed={closed} onCleared={load} />}
      <div className="grid gap-3" data-testid="entry-mode-buttons">
        {MODES.map(([k, label, sub, Icon]) => (
          <button key={k} type="button" onClick={() => setMode(k)} disabled={closed}
            className={`flex items-center gap-4 rounded-2xl border-2 px-5 py-4 text-left transition-colors duration-200 disabled:opacity-50 ${mode === k ? "border-[#011E38] bg-[#011E38] text-white" : "border-slate-200 bg-white text-[#011E38] hover:border-[#C5A059]"}`}
            data-testid={`entry-mode-${k}`}>
            <Icon className={`w-7 h-7 shrink-0 ${mode === k ? "text-[#C5A059]" : "text-[#011E38]"}`} />
            <span><span className="block font-display text-lg font-bold uppercase tracking-wide leading-tight">{label}</span>
              <span className={`block text-xs mt-0.5 ${mode === k ? "text-slate-300" : "text-slate-500"}`}>{sub}</span></span>
          </button>
        ))}
      </div>
      {blockMsg ? (
        <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-sm" data-testid="entry-mode-blocked">{blockMsg}</div>
      ) : mode === "perf" ? (
        <>
          {entry && !closed && <div className="text-sm text-sky-700 bg-sky-50 rounded-xl px-4 py-3" data-testid="entry-existing-note">You already submitted for this date{entry.no_walkin ? " (No Walk-in)" : ""}. Saving will update it.</div>}
          {hasStatus && !closed && <div className="text-sm text-amber-800 bg-amber-50 rounded-xl px-4 py-3" data-testid="entry-replace-status-note">Submitting performance will replace your {ds.status === "leave" ? "leave" : "week off"} for this day.</div>}
          <EntryForm date={date} entry={entry} cats={cats} closed={closed} onSaved={() => navigate("/me")} onDelete={() => setDel(true)} />
        </>
      ) : mode === "off" ? (
        <OffPicker date={date} onSaved={load} />
      ) : mode === "no_walkin" ? (
        <>
          {hasStatus && <div className="text-sm text-amber-800 bg-amber-50 rounded-xl px-4 py-3">This will replace your {ds.status === "leave" ? "leave" : "week off"} for this day.</div>}
          <NoWalkin date={date} entry={entry} onSaved={load} />
          {entry?.no_walkin && !closed && <Button variant="outline" onClick={() => setDel(true)} className="w-full h-12 rounded-2xl text-red-700 border-red-200" data-testid="no-walkin-delete-button">Delete this entry</Button>}
        </>
      ) : null}
      {del && <DeleteEntryDialog entry={entry} onClose={() => setDel(false)} onDone={() => { setDel(false); load(); }} />}
    </div>
  );
}
